import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import type { BatchRequest, BatchResponse, CommentData, ReadByEntry } from 'types'

const mergeReadBy = (existing: ReadByEntry[] | undefined, additions: ReadByEntry[]): ReadByEntry[] => {
  const map = new Map<string, ReadByEntry>()
  ;(existing ?? []).forEach(e => map.set(e.user_uuid, e))
  additions.forEach(a => {
    const prev = map.get(a.user_uuid)
    if (!prev) {
      map.set(a.user_uuid, a)
    } else {
      // keep newest readAt
      const prevTs = Date.parse(prev.readAt || '')
      const nextTs = Date.parse(a.readAt || '')
      map.set(a.user_uuid, nextTs > prevTs ? a : prev)
    }
  })
  return Array.from(map.values())
}

const updateCommentsArray = (bySk: Map<string, ReadByEntry[]>) => (
  old: CommentData[] | undefined
): CommentData[] | undefined => {
  if (!old || !Array.isArray(old)) return old
  let changed = false
  const next = old.map(c => {
    const add = c.SK ? bySk.get(c.SK) : undefined
    if (!add || add.length === 0) return c
    changed = true
    return { ...c, readBy: mergeReadBy(c.readBy, add) }
  })
  return changed ? next : old
}

const updateObjectWithCommentsTable = (bySk: Map<string, ReadByEntry[]>) => (old: any) => {
  if (!old || typeof old !== 'object' || !Array.isArray(old.commentsTable)) return old
  const nextComments = updateCommentsArray(bySk)(old.commentsTable)
  if (nextComments === old.commentsTable) return old
  return { ...old, commentsTable: nextComments }
}

const batchUpdateRead = async (payload: BatchRequest): Promise<BatchResponse> => {
  const url = `${baseUrl}/aws/updatemassitems`
  const { data, status } = await axios.put<BatchResponse>(url, payload)
  if (status !== 200) throw new Error('Network response was not ok')
  return data
}

export const useBatchReadComments = (): UseMutationResult<BatchResponse, unknown, BatchRequest> => {
  const queryClient = useQueryClient()

  return useMutation<BatchResponse, unknown, BatchRequest>({
    mutationFn: batchUpdateRead,
    onSuccess: (_data, variables) => {
      // Build SK -> additions[] (supports multiple comments in one batch)
      const bySk = new Map<string, ReadByEntry[]>()
      for (const u of variables.updates) {
        if (!u?.itemSortKey || !Array.isArray(u.newValue)) continue
        const prev = bySk.get(u.itemSortKey) ?? []
        bySk.set(u.itemSortKey, prev.concat(u.newValue))
      }

      // Update caches you keep:
      queryClient.setQueryData<CommentData[]>(['commentsTable'], updateCommentsArray(bySk))
      queryClient.setQueryData<any>(['entitiesFromAllItems'], updateObjectWithCommentsTable(bySk))

      // If you keep per-thread/per-task lists (e.g. ['commentsBySk', 'TASK_…']):
      const updatedGlobal = queryClient.getQueryData<CommentData[]>(['commentsTable'])
      if (updatedGlobal?.length) {
        const affected = updatedGlobal.filter(c => c.SK && bySk.has(c.SK))
        for (const c of affected) {
          const key = c.queryKey
          if (key && Array.isArray(key)) {
            queryClient.setQueryData<CommentData[]>(key, updateCommentsArray(bySk))
          }
        }
      }
    },
    onError: err => {
      console.error('[useBatchReadComments] Error updating readBy:', err)
    },
  })
}
